import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { useSpaceGroupResource } from "@circle-react/contexts/useSpaceGroupResource";
import { useAddMembersToSpaceGroup } from "@circle-react/hooks/useAddMembersToSpaceGroup";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import { AddMemberContent } from "./AddMemberContent";
import {
  ADD_SPACE_GROUP_MEMBERS_DEFAULT_VALUES,
  ADD_SPACE_GROUP_MEMBERS_FORM_ID,
  type AddSpaceGroupMemberModalProps,
} from "./types";

export const AddSpaceGroupMemberUIKitComponent = ({
  queryKey,
  spaceGroupId,
}: AddSpaceGroupMemberModalProps) => {
  const toastr = useToast();
  const queryClient = useQueryClient();
  const modal = useModal();
  const { refetch } = useSpaceGroupResource(spaceGroupId);
  const { mutate: addMembers, isPending: isAdding } = useAddMembersToSpaceGroup(
    {
      onError: (error: { message: string }) => {
        toastr.error(error.message);
      },
      onSuccess: ({ message }: { message: string }) => {
        if (queryKey) {
          void queryClient.invalidateQueries({ queryKey });
        }
        void queryClient.invalidateQueries({ queryKey: ["community-members"] });
        void refetch?.();
        toastr.success(message, {
          duration: "short",
          shouldUseProgress: false,
        });
        void modal.hide();
      },
    },
  );

  const onSubmit = (formContent: {
    add_all_members?: boolean;
    community_member_ids?: string[] | null;
  }) =>
    addMembers({
      spaceGroupId,
      communityMembersId: formContent.community_member_ids,
      shouldAddAllMembers: formContent.add_all_members,
    });

  return (
    <Modal
      isOpen={modal.visible}
      onClose={modal.hide}
      contentPortalId="add-space-group-member-portal-content"
      title={t("space_groups.form.edit.members.add_member")}
    >
      <Modal.Overlay />
      <Modal.Content size="md">
        <Form
          id={ADD_SPACE_GROUP_MEMBERS_FORM_ID}
          mode="onChange"
          onSubmit={onSubmit}
          defaultValues={ADD_SPACE_GROUP_MEMBERS_DEFAULT_VALUES}
          className="flex flex-col overflow-y-auto"
          formClassName="flex overflow-y-auto flex-col"
        >
          <AddMemberContent
            onClose={modal.hide}
            isLoading={isAdding}
            formId={ADD_SPACE_GROUP_MEMBERS_FORM_ID}
          />
        </Form>
      </Modal.Content>
    </Modal>
  );
};
